import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { SupportKbImport } from '@/components/SupportKbImport'
import { SupportCategory, SupportArticle, Toggle } from './shared'

type CategoryForm = { name: string; description: string }
type ArticleForm = {
  title: string
  content: string
  summary: string
  tags: string
  category_id: string
  is_active: boolean
}

const emptyCategoryForm: CategoryForm = { name: '', description: '' }
const emptyArticleForm: ArticleForm = { title: '', content: '', summary: '', tags: '', category_id: '', is_active: true }

export const SupportKnowledgeBase: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [categoryForm, setCategoryForm] = useState<CategoryForm | null>(null)
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)
  const [articleForm, setArticleForm] = useState<ArticleForm | null>(null)
  const [editingArticleId, setEditingArticleId] = useState<string | null>(null)
  const [formError, setFormError] = useState('')

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['support-module-categories'],
    queryFn: async () => {
      const response = await api.get('/support/categories')
      return (response.data.data || []) as SupportCategory[]
    },
  })

  const { data: articles, isLoading: articlesLoading } = useQuery({
    queryKey: ['support-module-articles', selectedCategory],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedCategory) params.append('categoryId', selectedCategory)
      params.append('includeInactive', 'true')
      const response = await api.get(`/support/articles?${params.toString()}`)
      return (response.data.data || []) as SupportArticle[]
    },
  })

  const invalidateKb = () => {
    queryClient.invalidateQueries({ queryKey: ['support-module-categories'] })
    queryClient.invalidateQueries({ queryKey: ['support-module-articles'] })
  }

  const saveCategoryMutation = useMutation({
    mutationFn: async (data: CategoryForm) => {
      if (editingCategoryId) {
        return api.put(`/support/categories/${editingCategoryId}`, data)
      }
      return api.post('/support/categories', data)
    },
    onSuccess: () => {
      setCategoryForm(null)
      setEditingCategoryId(null)
      setFormError('')
      invalidateKb()
    },
    onError: (error: any) => setFormError(error.response?.data?.error || t('supportModule.saveError')),
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/support/categories/${id}`),
    onSuccess: () => {
      if (selectedCategory) setSelectedCategory('')
      invalidateKb()
    },
  })

  const toggleCategoryMutation = useMutation({
    mutationFn: async (category: SupportCategory) =>
      api.put(`/support/categories/${category.id}`, { is_active: !category.is_active }),
    onSuccess: invalidateKb,
  })

  const saveArticleMutation = useMutation({
    mutationFn: async (form: ArticleForm) => {
      const payload = {
        title: form.title,
        content: form.content,
        summary: form.summary || undefined,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        category_id: form.category_id || null,
        is_active: form.is_active,
      }
      if (editingArticleId) {
        return api.put(`/support/articles/${editingArticleId}`, payload)
      }
      return api.post('/support/articles', payload)
    },
    onSuccess: () => {
      setArticleForm(null)
      setEditingArticleId(null)
      setFormError('')
      invalidateKb()
    },
    onError: (error: any) => setFormError(error.response?.data?.error || t('supportModule.saveError')),
  })

  const deleteArticleMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/support/articles/${id}`),
    onSuccess: invalidateKb,
  })

  const toggleArticleMutation = useMutation({
    mutationFn: async (article: SupportArticle) =>
      api.put(`/support/articles/${article.id}`, { is_active: !article.is_active }),
    onSuccess: invalidateKb,
  })

  const inputClass = 'w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Categories */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 h-fit">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('supportModule.categories')}</h2>
          <button
            onClick={() => { setCategoryForm({ ...emptyCategoryForm }); setEditingCategoryId(null); setFormError('') }}
            className="text-sm text-[#006cd4] hover:text-[#0052a3] transition-colors flex items-center gap-1"
          >
            <Icon name="plus" /> {t('supportModule.addCategory')}
          </button>
        </div>

        {categoryForm && (
          <form
            onSubmit={(e) => { e.preventDefault(); if (categoryForm.name.trim()) saveCategoryMutation.mutate(categoryForm) }}
            className="mb-4 p-4 bg-[#1a1a25] rounded-lg space-y-3"
          >
            <input
              type="text"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder={t('supportModule.categoryName')}
              className={inputClass}
              required
            />
            <input
              type="text"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              placeholder={t('supportModule.categoryDesc')}
              className={inputClass}
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saveCategoryMutation.isPending}
                className="flex-1 py-2 bg-[#006cd4] text-white text-sm font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => { setCategoryForm(null); setEditingCategoryId(null); setFormError('') }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}

        {categoriesLoading ? (
          <div className="text-gray-400 text-center py-4 text-sm">{t('supportModule.loading')}</div>
        ) : !categories || categories.length === 0 ? (
          <div className="text-gray-400 text-center py-4 text-sm">{t('supportModule.noCategories')}</div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => setSelectedCategory('')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedCategory === '' ? 'bg-[#006cd4]/20 text-[#006cd4]' : 'text-gray-300 hover:bg-white/5'
              }`}
            >
              {t('supportModule.allArticles')}
            </button>
            {categories.map((category) => (
              <div
                key={category.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                  selectedCategory === category.id ? 'bg-[#006cd4]/20 text-[#006cd4]' : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <span className={`flex-1 truncate ${category.is_active ? '' : 'opacity-50 line-through'}`}>
                  {category.name}
                </span>
                <span onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <Toggle checked={category.is_active} onChange={() => toggleCategoryMutation.mutate(category)} label={t('supportModule.active')} />
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setCategoryForm({ name: category.name, description: category.description || '' })
                    setEditingCategoryId(category.id)
                    setFormError('')
                  }}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity"
                  title={t('supportModule.editCategory')}
                >
                  <Icon name="pen" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm(t('supportModule.deleteCategoryConfirm'))) deleteCategoryMutation.mutate(category.id)
                  }}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 transition-opacity"
                  title={t('common.delete')}
                >
                  <Icon name="trash" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Articles */}
      <div className="lg:col-span-2 bg-[#12121a] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('supportModule.articles')}</h2>
          <button
            onClick={() => {
              setArticleForm({ ...emptyArticleForm, category_id: selectedCategory })
              setEditingArticleId(null)
              setFormError('')
            }}
            className="text-sm text-[#006cd4] hover:text-[#0052a3] transition-colors flex items-center gap-1"
          >
            <Icon name="plus" /> {t('supportModule.addArticle')}
          </button>
        </div>

        {/* Import from Markdown — same drop-zone style as Widget AI configuration */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-300 mb-1">{t('supportModule.importArticlesTitle')}</h3>
          <p className="text-xs text-gray-500 mb-3">{t('supportModule.importArticlesDesc')}</p>
          <SupportKbImport />
        </div>

        {formError && !categoryForm && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">{formError}</div>
        )}

        {articleForm && (
          <form
            onSubmit={(e) => { e.preventDefault(); if (articleForm.title.trim() && articleForm.content.trim()) saveArticleMutation.mutate(articleForm) }}
            className="mb-6 p-4 bg-[#1a1a25] rounded-lg space-y-3"
          >
            <h3 className="font-medium text-sm text-gray-300">
              {editingArticleId ? t('supportModule.editArticle') : t('supportModule.addArticle')}
            </h3>
            <input
              type="text"
              value={articleForm.title}
              onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
              placeholder={t('supportModule.articleTitle')}
              className={inputClass}
              required
            />
            <textarea
              value={articleForm.content}
              onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
              placeholder={t('supportModule.articleContent')}
              rows={6}
              className={inputClass}
              required
            />
            <input
              type="text"
              value={articleForm.summary}
              onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
              placeholder={t('supportModule.articleSummary')}
              className={inputClass}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={articleForm.tags}
                onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                placeholder={t('supportModule.articleTags')}
                className={inputClass}
              />
              <select
                value={articleForm.category_id}
                onChange={(e) => setArticleForm({ ...articleForm, category_id: e.target.value })}
                className={inputClass}
              >
                <option value="">{t('supportModule.uncategorized')}</option>
                {(categories || []).map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <Toggle checked={articleForm.is_active} onChange={(v) => setArticleForm({ ...articleForm, is_active: v })} label={t('supportModule.active')} />
              {t('supportModule.active')}
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saveArticleMutation.isPending}
                className="flex-1 py-2 bg-[#006cd4] text-white text-sm font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {saveArticleMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => { setArticleForm(null); setEditingArticleId(null); setFormError('') }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}

        {articlesLoading ? (
          <div className="text-gray-400 text-center py-8">{t('supportModule.loading')}</div>
        ) : !articles || articles.length === 0 ? (
          <div className="text-gray-400 text-center py-8">
            <p className="text-lg mb-2">{t('supportModule.noArticles')}</p>
            <p className="text-sm">{t('supportModule.noArticlesDesc')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {articles.map((article) => (
              <div key={article.id} className="p-4 bg-[#1a1a25] rounded-lg border border-transparent hover:border-white/10 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className={`font-medium ${article.is_active ? '' : 'opacity-50'}`}>{article.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{article.category_name || t('supportModule.uncategorized')}</span>
                      <span className="flex items-center gap-1">
                        <Icon name="eye" /> {article.view_count} {t('supportModule.views')}
                      </span>
                      <span className={article.is_active ? 'text-green-400' : 'text-gray-500'}>
                        {article.is_active ? t('supportModule.active') : t('supportModule.inactive')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Toggle checked={article.is_active} onChange={() => toggleArticleMutation.mutate(article)} label={t('supportModule.active')} />
                    <button
                      onClick={() => {
                        setArticleForm({
                          title: article.title,
                          content: article.content,
                          summary: article.summary || '',
                          tags: (article.tags || []).join(', '),
                          category_id: article.category_id || '',
                          is_active: article.is_active,
                        })
                        setEditingArticleId(article.id)
                        setFormError('')
                      }}
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title={t('supportModule.editArticle')}
                    >
                      <Icon name="pen" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(t('supportModule.deleteArticleConfirm'))) deleteArticleMutation.mutate(article.id)
                      }}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title={t('common.delete')}
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </div>
                {article.summary && <p className="text-sm text-gray-400 mt-2 line-clamp-2">{article.summary}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
